import React from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { TextAreaController } from '@components-clean/core/text-area';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError } from '@utils/notification';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';
import SubjectReviewLogsApi from '@apis/bff-api/subject-monitoring/subject-review-log';

export interface SubjectReviewLogDeleteDialogProps {
    recordId: string;
    onClose: () => void;
    studyRef: string;
    onDeleteHandler: (success: boolean | undefined) => void;
    reviewLogId: number;
    observationId?: string | null;
    reviewLogDatasetDate?: string | null;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteSubjectReviewLogDialog = ({
    recordId,
    onClose,
    studyRef,
    onDeleteHandler,
    reviewLogId,
    observationId,
    reviewLogDatasetDate,
}: SubjectReviewLogDeleteDialogProps) => {
    const { t } = useTranslation('sm');
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });
    const { trigger: deleteReviewLog } = SubjectReviewLogsApi.useDelete(studyRef);
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const { data: observation, isLoading: isValidating } = SubjectObservationsApi.useGetDetails(
        studyRef,
        observationId!,
    );

    const onDelete = async (data: FormInputs) => {
        try {
            const response = await deleteReviewLog({
                deletedReason: data.deleteReason,
                reviewLogId,
                observationId: Number(observationId),
                dataSetDate: reviewLogDatasetDate,
            });
            onDeleteHandler(response);
        } catch {
            ShowError(t('common:errorMessage'));
        }
    };

    const maxChars = 350;

    // Only show up to 350 characters and then append ...
    const truncatedObs =
        observation?.observation && observation.observation.length > maxChars
            ? `${String(observation.observation).slice(0, maxChars)}...`
            : observation?.observation;

    // We need to append the additional height to the dialog depending on the observation length to avoid scroll bar. We need to show a maximum of
    // 5 lines for observation text which is approx 350 chars. Calculating the no of lines and then appending
    // 15 px height per line. 280 is the minimum required length. 65 px is if the number of lines is more than 5.

    const minDialogHeight = 280;
    const allowedAdditionalMaxHeight = 65;
    const approxCharsPerLine = 70;

    const calculatedHeight =
        (observation?.observation?.length as unknown as number) > maxChars
            ? allowedAdditionalMaxHeight
            : ((observation?.observation?.length as unknown as number) / approxCharsPerLine) * 15;

    const warningText = (
        <span className={cn('k-w-full k-d-flex k-flex-column k-gap-2', styles.fontSize)}>
            <div>
                <span className="k-font-medium k-color-primary">
                    {mtkTranslation('reviewLogGrid.deleteReviewLogWarning1')}{' '}
                </span>
                <span className="k-font-medium k-color-secondary">
                    {stringDateWithoutTimeFromUtc(reviewLogDatasetDate, 'DD-MMM-YYYY')}
                </span>
            </div>
            <div className={style.deleteWarningOverflow}>
                <span className="k-font-medium">{mtkTranslation('reviewLogGrid.deleteReviewLogWarning2')}</span>{' '}
                <span className={style.textHighLightNoBold}>{truncatedObs}</span>
            </div>
        </span>
    );

    const dialogTitle = `${mtkTranslation('reviewLogGrid.deleteReviewLog')}: ${recordId}`;
    const handlePrimarySubmit = () => handleSubmit((data) => onDelete(data))();

    return (
        <DraggableDialog
            dataTestId="subject-review-log-delete-dialog"
            onSubmit={handlePrimarySubmit}
            onClose={onClose}
            title={dialogTitle}
            initialHeight={minDialogHeight + calculatedHeight}
            initialWidth={520}
            primaryBtnLabel={t('common:delete')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {isValidating ? (
                    <Loading />
                ) : (
                    <>
                        <InfoField type="Warning" message={warningText} />
                        <TextAreaController
                            className={style.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="deleteReason"
                            required
                            data-testid="delete-subject-review-log"
                            label={`${t('deleteReasonLabel')} *`}
                            readOnly={isSubmitting}
                        />
                    </>
                )}
            </div>
        </DraggableDialog>
    );
};

export default DeleteSubjectReviewLogDialog;
